import React from "react";
import { Phone, PhoneOff, MonitorUp } from "lucide-react";
import { callStatusLabel } from "@/components/messenger/callHelpers";

const Btn = ({ onClick, cls, testid, title, children }) => (
  <button onClick={onClick} className={`w-16 h-16 rounded-full grid place-items-center ${cls}`} data-testid={testid} title={title} aria-label={title}>{children}</button>
);

// Remote audio always plays through <audio>; the remote <video> is muted so audio is never doubled.
export function CallOverlay({ call, name, refs, onAccept, onDecline, onEnd, screenSharing, onToggleShare, allowScreenShare = true }) {
  const live = call.mode === "live";
  const video = call.kind === "video" && call.mode !== "ended";
  return (
    <div className="fixed inset-0 bg-black/90 flex items-center justify-center p-4 overflow-y-auto" style={{ zIndex: "var(--z-modal)" }}
         data-testid="call-overlay" role="dialog" aria-label="Call">
      <audio ref={refs.remoteAudio} autoPlay />
      <div className="w-full max-w-md text-white text-center">
        <div className="text-xs uppercase tracking-widest text-white/70 mb-1" data-testid="call-status" aria-live="polite">
          {callStatusLabel(call)} · {call.kind}
        </div>
        <div className="text-2xl font-black truncate">{name || "User"}</div>
        {video ? (
          <div className="mt-4 relative bg-stone-900 rounded-2xl overflow-hidden aspect-[3/4] sm:aspect-video max-h-[60dvh] mx-auto">
            <video ref={refs.remoteVideo} autoPlay playsInline muted className="w-full h-full object-cover" data-testid="call-remote-video" />
            {!(live && call.net === "connected") && (
              <div className="absolute inset-0 grid place-items-center text-sm text-white/70" data-testid="call-video-waiting">{callStatusLabel(call)}</div>
            )}
            <video ref={refs.localVideo} autoPlay playsInline muted className="absolute bottom-2 right-2 w-20 h-28 sm:w-24 sm:h-32 rounded-lg object-cover border-2 border-white/40 bg-black" data-testid="call-local-video" />
          </div>
        ) : (
          <div className="mt-6 w-32 h-32 mx-auto rounded-full bg-gradient-to-br from-indigo-500 to-fuchsia-700 grid place-items-center" data-testid="call-audio-avatar">
            <Phone size={48} />
          </div>
        )}
        {call.mode !== "ended" && (
          <div className="mt-6 flex items-center justify-center gap-4">
            {call.mode === "incoming" && <Btn onClick={onAccept} cls="bg-emerald-500 hover:bg-emerald-600" testid="call-accept" title="Accept"><Phone size={24} /></Btn>}
            {live && allowScreenShare && (
              <Btn onClick={onToggleShare} cls={screenSharing ? "bg-amber-500 animate-pulse" : "bg-white/15 hover:bg-white/25"} testid="call-screen-share"
                   title={screenSharing ? "Stop sharing screen" : "Share screen"}><MonitorUp size={22} /></Btn>
            )}
            {call.mode === "incoming"
              ? <Btn onClick={onDecline} cls="bg-red-600 hover:bg-red-700" testid="call-decline" title="Decline"><PhoneOff size={24} /></Btn>
              : <Btn onClick={onEnd} cls="bg-red-600 hover:bg-red-700" testid="call-end" title="Hang up"><PhoneOff size={24} /></Btn>}
          </div>
        )}
        {live && screenSharing && <div className="mt-3 text-[11px] text-amber-300 uppercase tracking-widest font-bold">You're sharing your screen</div>}
      </div>
    </div>
  );
}
